import * as THREE from 'three';
import type { Yard } from './Yard';
import type { Colliders } from './Colliders';

/**
 * The land beyond the fence: a lane from the gate heading west, a few neighbours'
 * houses along it, and trees, bushes and rocks scattered to the horizon.
 * Seeded, so every phone on a license sees the same place.
 *
 * A big space costs nothing by itself; what costs is what's on screen. So the
 * repeated things are InstancedMeshes (one draw call per kind, low-poly), the
 * houses are a handful of boxes each, and the fog takes over at the edge.
 */
const WORLD_R = 88;

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const v = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _p = new THREE.Vector3();
const _c = new THREE.Color();

interface HouseSpec {
  t: number; // along the lane, 0..1
  side: 1 | -1;
  w: number;
  d: number;
  h: number;
  wall: number;
  roof: number;
}

export class Neighbourhood {
  readonly root = new THREE.Group();
  /** the lane's centreline (world), gate first */
  readonly lane: THREE.Vector3[];
  private windows: THREE.MeshStandardMaterial[] = [];
  private houseAt: { x: number; z: number; r: number }[] = [];

  constructor(scene: THREE.Object3D, yard: Yard, colliders: Colliders) {
    const rnd = mulberry32(20241111);
    // --- the lane: out of the gate, west along the back of the yard, then away
    this.lane = [yard.gateOutside.clone(), v(2.5, 0, -9.3), v(-3, 0, -9.2), v(-9, 0, -10), v(-16, 0, -13), v(-26, 0, -17), v(-38, 0, -24), v(-50, 0, -34)];
    yard.addPath(scene, this.lane, 1.1);
    const curve = new THREE.CatmullRomCurve3(this.lane, false, 'catmullrom', 0.4);

    // --- neighbours: alternating sides of the lane, each facing it
    const specs: HouseSpec[] = [
      { t: 0.44, side: 1, w: 5.2, d: 4.2, h: 2.7, wall: 0xdccbaa, roof: 0x6e4a38 },
      { t: 0.55, side: -1, w: 4.4, d: 4.0, h: 2.5, wall: 0xf1ede3, roof: 0x4a5a66 },
      { t: 0.66, side: 1, w: 6.0, d: 4.6, h: 3.0, wall: 0xb8c4b0, roof: 0x5a3f33 },
      { t: 0.76, side: -1, w: 4.8, d: 4.2, h: 2.6, wall: 0xe4d2c4, roof: 0x3f4f5a },
      { t: 0.86, side: 1, w: 5.4, d: 4.4, h: 2.8, wall: 0xd9d9cf, roof: 0x7a4c3a },
      { t: 0.95, side: -1, w: 6.4, d: 5.0, h: 3.1, wall: 0xc8b89a, roof: 0x4d3b33 },
    ];
    const tan = new THREE.Vector3();
    const n = new THREE.Vector3();
    for (const s of specs) {
      const p = curve.getPoint(s.t);
      curve.getTangent(s.t, tan).normalize();
      n.set(-tan.z, 0, tan.x).multiplyScalar(s.side); // off to one side
      const pos = p.clone().addScaledVector(n, 6.5 + s.d / 2);
      const yaw = Math.atan2(-n.x, -n.z); // the front (+z in house space) faces the lane
      this.house(pos.x, pos.z, yaw, s, colliders, rnd);
      this.houseAt.push({ x: pos.x, z: pos.z, r: Math.hypot(s.w, s.d) / 2 + 2 });
      // a short path to the door
      yard.addPath(scene, [p.clone(), p.clone().addScaledVector(n, 3.2), pos.clone().addScaledVector(n, -s.d / 2 - 0.3)], 0.5);
    }

    // --- scatter: keep clear of the yard, the campus past the fence, the lane and the houses
    const lanePts = curve.getSpacedPoints(80);
    const clear = (x: number, z: number, margin: number): boolean => {
      if (Math.hypot(x, z) < 12 + margin) return false; // the yard
      if (Math.hypot(x - 3, z + 13) < 13 + margin) return false; // Whitman
      for (const h of this.houseAt) if (Math.hypot(x - h.x, z - h.z) < h.r + margin) return false;
      for (const q of lanePts) if (Math.hypot(x - q.x, z - q.z) < 2.2 + margin) return false;
      return true;
    };
    const spot = (rMin: number, rMax: number, margin: number): THREE.Vector3 | null => {
      for (let i = 0; i < 40; i++) {
        const a = rnd() * Math.PI * 2;
        const r = rMin + Math.sqrt(rnd()) * (rMax - rMin);
        const x = Math.cos(a) * r;
        const z = Math.sin(a) * r;
        if (clear(x, z, margin)) return v(x, 0, z);
      }
      return null;
    };

    // trees: trunk + four canopy balls, each an InstancedMesh
    const trees: { p: THREE.Vector3; s: number; yaw: number }[] = [];
    for (let i = 0; i < 44; i++) {
      const p = spot(15, WORLD_R - 6, 1.5);
      if (!p) continue;
      const s = 0.9 + rnd() * 0.7;
      trees.push({ p, s, yaw: rnd() * Math.PI * 2 });
      colliders.cylinder('tree', p.x, p.z, 0.22 * s, 2.2 * s);
    }
    // instanced meshes are culled as a whole, so build them per quadrant: what's behind you isn't drawn
    const quadrant = (p: THREE.Vector3): number => (p.x >= 0 ? 0 : 1) + (p.z >= 0 ? 0 : 2);
    const bark = new THREE.MeshStandardMaterial({ color: 0x6b4a2e, roughness: 1 });
    const leaf = new THREE.MeshStandardMaterial({ color: 0x4f7a3a, roughness: 0.95 });
    const trunkGeo = new THREE.CylinderGeometry(0.16, 0.24, 2.2, 7);
    const ballGeo = new THREE.SphereGeometry(1, 7, 5);
    for (let q = 0; q < 4; q++) {
      const mine = trees.filter((t) => quadrant(t.p) === q);
      if (!mine.length) continue;
      const trunk = new THREE.InstancedMesh(trunkGeo, bark, mine.length);
      this.place(trunk, mine, v(0, 1.1, 0), 1);
      this.root.add(trunk);
      for (const [x, y, z, r] of [
        [0, 2.6, 0, 1.1],
        [0.7, 2.2, 0.3, 0.8],
        [-0.6, 2.3, -0.4, 0.85],
        [0.1, 3.2, -0.2, 0.7],
      ] as const) {
        const ball = new THREE.InstancedMesh(ballGeo, leaf, mine.length);
        this.place(ball, mine, v(x, y, z), r);
        mine.forEach((_t, i) => ball.setColorAt(i, _c.setHSL(0.27 + ((i * 7) % 5) * 0.012, 0.42, 0.33 + ((i * 3) % 4) * 0.02)));
        this.root.add(ball);
      }
    }

    // bushes: squashed icosahedra in a few greens
    const bushes: { p: THREE.Vector3; s: number; yaw: number }[] = [];
    for (let i = 0; i < 110; i++) {
      const p = spot(11, WORLD_R - 3, 0.3);
      if (p) bushes.push({ p, s: 0.45 + rnd() * 0.5, yaw: rnd() * Math.PI * 2 });
    }
    // a few hug the houses
    for (const h of this.houseAt) {
      for (let k = 0; k < 4; k++) {
        const a = rnd() * Math.PI * 2;
        bushes.push({ p: v(h.x + Math.cos(a) * (h.r - 1.2), 0, h.z + Math.sin(a) * (h.r - 1.2)), s: 0.35 + rnd() * 0.3, yaw: rnd() * 6 });
      }
    }
    const bushGeo = new THREE.IcosahedronGeometry(1, 1);
    const bushMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1 });
    for (let q = 0; q < 4; q++) {
      const mine = bushes.filter((b) => quadrant(b.p) === q);
      if (!mine.length) continue;
      const bush = new THREE.InstancedMesh(bushGeo, bushMat, mine.length);
      mine.forEach((b, i) => {
        _q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), b.yaw);
        _s.set(b.s, b.s * 0.72, b.s * (0.85 + ((i * 5) % 3) * 0.1));
        _p.set(b.p.x, b.s * 0.55, b.p.z);
        bush.setMatrixAt(i, _m.compose(_p, _q, _s));
        bush.setColorAt(i, _c.setHSL(0.24 + ((i * 11) % 7) * 0.012, 0.45, 0.3 + ((i * 3) % 5) * 0.025));
      });
      bush.receiveShadow = true; // low and round: their own shadows aren't worth a second pass
      this.root.add(bush);
    }

    // rocks
    const rocks: { p: THREE.Vector3; s: number; yaw: number }[] = [];
    for (let i = 0; i < 26; i++) {
      const p = spot(12, WORLD_R - 3, 0.2);
      if (p) rocks.push({ p, s: 0.22 + rnd() * 0.45, yaw: rnd() * Math.PI * 2 });
    }
    const rock = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(1, 0), new THREE.MeshStandardMaterial({ color: 0x8e8a80, roughness: 1 }), rocks.length);
    rocks.forEach((r, i) => {
      _q.setFromEuler(new THREE.Euler(rnd() * 0.5, r.yaw, rnd() * 0.5));
      _s.set(r.s * 1.3, r.s * 0.8, r.s);
      _p.set(r.p.x, r.s * 0.35, r.p.z);
      rock.setMatrixAt(i, _m.compose(_p, _q, _s));
    });
    rock.castShadow = true;
    this.root.add(rock);

    scene.add(this.root);
  }

  /** fill an InstancedMesh from tree placements: `offset` and `radius` are in tree space (scaled by the tree) */
  private place(mesh: THREE.InstancedMesh, trees: { p: THREE.Vector3; s: number; yaw: number }[], offset: THREE.Vector3, radius: number): void {
    trees.forEach((t, i) => {
      _q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), t.yaw);
      _p.copy(offset).multiplyScalar(t.s).applyQuaternion(_q).add(t.p);
      _s.setScalar(radius * t.s);
      mesh.setMatrixAt(i, _m.compose(_p, _q, _s));
    });
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  }

  /** a neighbour's house: walls, a gable roof, a door, windows that glow at night, a chimney */
  private house(x: number, z: number, yaw: number, s: HouseSpec, colliders: Colliders, rnd: () => number): void {
    const g = new THREE.Group();
    g.position.set(x, 0, z);
    g.rotation.y = yaw;
    const wall = new THREE.MeshStandardMaterial({ color: s.wall, roughness: 0.95 });
    const roofMat = new THREE.MeshStandardMaterial({ color: s.roof, roughness: 1 });
    const trim = new THREE.MeshStandardMaterial({ color: 0x5b4636, roughness: 0.9 });
    const win = new THREE.MeshStandardMaterial({ color: 0x2f3a44, roughness: 0.35 });
    this.windows.push(win);
    const body = new THREE.Mesh(new THREE.BoxGeometry(s.w, s.h, s.d), wall);
    body.position.y = s.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    g.add(body);
    // gable roof: a prism along x, eaves past the walls
    const rh = 0.45 * s.d;
    const ox = s.w / 2 + 0.3;
    const oz = s.d / 2 + 0.3;
    const roof = new THREE.BufferGeometry();
    const P = [
      [-ox, s.h, oz], [ox, s.h, oz], [ox, s.h + rh, 0], [-ox, s.h + rh, 0], // front slope
      [-ox, s.h, -oz], [ox, s.h, -oz], // back eaves
    ];
    roof.setAttribute('position', new THREE.Float32BufferAttribute(P.flat(), 3));
    roof.setIndex([0, 1, 2, 0, 2, 3, 5, 4, 3, 5, 3, 2, 4, 0, 3, 1, 5, 2]);
    roof.computeVertexNormals();
    const roofMesh = new THREE.Mesh(roof, roofMat);
    roofMesh.castShadow = true;
    g.add(roofMesh);
    // door on the front (+z), windows either side and on the back
    const door = new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.9, 0.08), trim);
    door.position.set(-s.w * 0.22, 0.95, s.d / 2 + 0.03);
    g.add(door);
    const winGeo = new THREE.BoxGeometry(0.8, 0.9, 0.06);
    for (const [wx, wz] of [
      [s.w * 0.22, s.d / 2 + 0.03],
      [-s.w * 0.32, -s.d / 2 - 0.03],
      [s.w * 0.28, -s.d / 2 - 0.03],
    ]) {
      const w = new THREE.Mesh(winGeo, win);
      w.position.set(wx, 1.45, wz);
      g.add(w);
    }
    if (s.h > 2.6) {
      const w = new THREE.Mesh(winGeo, win);
      w.position.set(s.w * 0.22, s.h + rh * 0.35, s.d / 2 + 0.03);
      w.scale.set(0.8, 0.7, 1);
      g.add(w);
    }
    const chimney = new THREE.Mesh(new THREE.BoxGeometry(0.5, 1.2, 0.5), trim);
    chimney.position.set(s.w * (0.3 - rnd() * 0.6), s.h + rh * 0.55, s.d * 0.18);
    chimney.castShadow = true;
    g.add(chimney);
    g.traverse((o) => (o.userData.tapName = 'neighbour'));
    this.root.add(g);
    colliders.box('house', v(x, s.h / 2, z), yaw, v(s.w / 2, s.h / 2, s.d / 2));
  }

  /** 0 day .. 1 night: the neighbours' windows come on */
  setNight(n: number): void {
    for (const m of this.windows) {
      m.emissive.set(0xffc978);
      m.emissiveIntensity = 1.4 * n;
    }
  }
}
